import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from '@/components/common/text';
import type { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminErrorBoundary } from '@/components';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { TodoListForm } from '@/components/admin/todoLists/TodoListForm';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import {
  useGetTodoListQuery,
  useUpdateTodoListMutation,
} from '@/store/api/admin/todoListsApi';
import { extractTodoListError } from './todoListErrors';
import type { AdminStackParamList } from '@/navigation/admin/AdminNavigator';

type Props = StackScreenProps<AdminStackParamList, 'EditTodoList'>;

/**
 * Edit an existing to-do list, pre-filled from `useGetTodoListQuery`. The single
 * update PATCH carries the edit-only "apply now" toggle (default ON) as
 * `applyNow`, so the server re-syncs the current period's occurrence atomically
 * in the same transaction — no separate request, no race. On success we navigate
 * back to the occurrence screen with a one-shot `savedBanner` param so the
 * confirmation shows on the checklist the user lands on. 409s (list_archived /
 * no_current_occurrence) surface as an inline error.
 */
export const EditTodoListScreen: React.FC<Props> = ({ navigation, route }) => {
  const { listId } = route.params;
  const { layoutMode } = useResponsive();
  const { redesign } = useAdminColors();

  const {
    data: list,
    isLoading,
    error: loadError,
  } = useGetTodoListQuery(listId);
  const [updateTodoList, { isLoading: isSaving }] = useUpdateTodoListMutation();

  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (payload: any) => {
      setSubmitError(null);
      try {
        await updateTodoList({ listId, ...payload }).unwrap();
        // React Navigation merges these params into the TodoListOccurrence
        // instance already below us in the stack: this both pops back to it and
        // hands it the save confirmation in one step. The variant mirrors the
        // apply-now toggle that rode in the payload.
        navigation.navigate('TodoListOccurrence', {
          listId,
          savedBanner: payload.applyNow ? 'saved-applied' : 'saved',
        });
      } catch (err) {
        setSubmitError(extractTodoListError(err));
      }
    },
    [listId, navigation, updateTodoList],
  );

  const handleCancel = useCallback(() => navigation.goBack(), [navigation]);

  if (isLoading && !list) {
    return (
      <AdminErrorBoundary>
        <View style={styles.centered}>
          <ActivityIndicator color={redesign.text.secondary} />
        </View>
      </AdminErrorBoundary>
    );
  }

  if (!list) {
    return (
      <AdminErrorBoundary>
        <BaseAdminScreen
          screenName="EditTodoList"
          navigation={navigation}
          showPlaceholder={false}
          scrollEnabled={false}
          contentContainerStyle={styles.baseScreenContent}
        >
          <View style={styles.centered}>
            <Text style={[styles.stateText, { color: redesign.text.muted }]}>
              {loadError ? 'This to-do list is unavailable.' : 'Loading…'}
            </Text>
          </View>
        </BaseAdminScreen>
      </AdminErrorBoundary>
    );
  }

  const form = (
    <TodoListForm
      mode="edit"
      propertyId={list.propertyId}
      initial={list}
      isSubmitting={isSaving}
      submitError={submitError}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary>
        <DesktopAdminLayout>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.desktopContent}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
          >
            <DesktopPageHeader title="Edit to-do list" subtitle={list.title} />
            <GlassCard padding={24} style={styles.card}>
              {form}
            </GlassCard>
          </ScrollView>
        </DesktopAdminLayout>
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="EditTodoList"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.mobileContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View>{form}</View>
        </ScrollView>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  desktopContent: {
    paddingBottom: 24,
    gap: 16,
    maxWidth: 880,
    alignSelf: 'center',
    width: '100%',
  },
  mobileContent: { padding: 16, paddingBottom: 32 },
  card: { gap: 16 },
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  centered: {
    flex: 1,
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  stateText: { fontSize: 15, textAlign: 'center' },
});
